/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Injectable,
  signal,
  inject,
  TransferState,
  makeStateKey,
  StateKey,
  computed,
} from '@angular/core';
import { Router } from '@angular/router';
import { BaseUser, LoadingService } from '@awo-care/shared-core';
import { AuthRoutingService } from '@awo-care/shared-auth';
import { sanitizeForFirestore } from '@awo-care/utils';
import { Firestore, collection, doc, getDoc, setDoc } from 'firebase/firestore';
import { Auth, User } from 'firebase/auth';
import { FIREBASE_AUTH, FIREBASE_FIRESTORE } from '@awo-care/shared-firebase';

const LOGIN_URL = 'auth/login';

const claimHierarchy: { [key: string]: string[] } = {
  user: ['admin'],
  admin: [],
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  auth = inject(FIREBASE_AUTH);
  loadingService = inject(LoadingService);
  authRoutingService = inject(AuthRoutingService);

  public currentUser = signal({} as User);
  public isLoggedIn = signal(false);
  private userClaimsSignal = signal({} as unknown);
  public userClaims = this.userClaimsSignal.asReadonly();
  private firestore: Firestore = inject(FIREBASE_FIRESTORE);
  public isInitialized = signal(false);
  usersCollection = collection(this.firestore, 'users');

  private readonly AUTH_USER_KEY: StateKey<User> =
    makeStateKey<User>('authUser');
  private REDIRECT_KEY = makeStateKey<string>('redirect');

  router = inject(Router);
  transferState = inject(TransferState);

  constructor() {
    this.auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          this.currentUser.set(user);
          this.userClaimsSignal.set(tokenResult.claims);
          if (!user.emailVerified) {
            this.isLoggedIn.set(false);
          } else {
            this.isLoggedIn.set(true);
          }
        } catch (error) {
          console.error('Error processing auth state:', { uid: user.uid, error });
          const err = error as GlobalError;
          err.header = 'Anmeldefehler';
          err.message = 'Die Anmeldung konnte nicht verarbeitet werden. Bitte laden Sie die Seite neu.';
          err.bugReport = 'manual';
          this.isLoggedIn.set(false);
          throw err;
        } finally {
          this.isInitialized.set(true);
        }
      } else {
        this.isLoggedIn.set(false);
        this.isInitialized.set(true);
      }
    });
  }

  loadClaims(): Promise<boolean> {
    return new Promise((resolve) => {
      const unsubscribe = this.auth.onAuthStateChanged((user) => {
        unsubscribe();
        if (user) {
          user.getIdTokenResult().then((tokenResult) => {
            this.userClaimsSignal.set(tokenResult.claims);
            resolve(true);
          });
        } else {
          resolve(false);
        }
      });
    });
  }

  hasClaim = createMemoizedSelector((claim: string): boolean => {
    if (!this.userClaims()) return false;
    const checkClaim = (claim: string): boolean => {
      const claims = this.userClaims() as { [key: string]: unknown };
      if (claims[claim]) return true;
      const parents = claimHierarchy[claim] || [];
      return parents.some((parentClaim) => checkClaim(parentClaim));
    };
    return checkClaim(claim);
  });

  isUser = computed(() => {
    return this.hasClaim('user') && !this.hasClaim('admin');
  });

  isAdmin = computed(() => {
    return this.hasClaim('admin');
  });

  getUserRole(): 'admin' | 'user' | undefined {
    if (this.isAdmin()) {
      return 'admin';
    } else if (this.isUser()) {
      return 'user';
    } else return undefined;
  }

  checkForLoginState(user: User) {
    if (user.uid) {
      // Only set as logged in if user has uid AND email is verified
      // unless it's their first login with the same provider
      const isFirstLogin =
        user.metadata.creationTime === user.metadata.lastSignInTime;
      this.isLoggedIn.set(user.emailVerified || isFirstLogin);
    } else {
      this.isLoggedIn.set(false);
    }
  }

  async signOut() {
    try {
      // Clear all states in one place
      this.userClaimsSignal.set({});
      this.currentUser.set({} as User);
      this.isLoggedIn.set(false);

      // Clear storage
      sessionStorage.clear();
      this.transferState.remove(this.AUTH_USER_KEY);
      this.transferState.remove(this.REDIRECT_KEY);

      // Sign out from Firebase
      await this.auth.signOut();

      // Navigate to login
      await this.router.navigateByUrl(LOGIN_URL);
    } catch (error) {
      console.error('Error during sign out:', { uid: this.currentUser()?.uid, error });
      const err = error as GlobalError;
      err.header = 'Abmeldefehler';
      err.message = 'Die Abmeldung konnte nicht vollständig durchgeführt werden. Sie wurden trotzdem abgemeldet.';
      err.bugReport = 'manual';
      // Still try to navigate to login even if there's an error
      await this.router.navigateByUrl(LOGIN_URL);
      throw err;
    }
  }

  /**
   * Handle successful login - redirect to portal
   */
  handleLoginSuccess(): void {
    this.authRoutingService.handleLoginSuccess();
  }

  async updateUser(uid: string, data: any) {
    const docRef = doc(this.usersCollection, uid);
    return await setDoc(docRef, sanitizeForFirestore(data), { merge: true });
  }

  async getUser(uid: string): Promise<BaseUser | null> {
    try {
      const docRef = doc(this.usersCollection, uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data() as BaseUser;
      } else {
        console.log('No such user document!');
        return null;
      }
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('Missing or insufficient permissions')
      ) {
        return null;
      }
      console.error('Error fetching user:', { uid, error });
      const err = error as GlobalError;
      err.header = 'Benutzerdaten nicht verfügbar';
      err.message = 'Die Benutzerdaten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.';
      err.bugReport = 'manual';
      throw err;
    }
  }
}

function createMemoizedSelector<T, R>(selector: (arg: T) => R): (arg: T) => R {
  const cache = new Map<T, R>();
  return (arg: T) => {
    if (cache.has(arg)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return cache.get(arg)!;
    } else {
      const result = selector(arg);
      cache.set(arg, result);
      return result;
    }
  };
}
